import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { BRAND } from '../config/branding';
import './Login.css';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // MFA State
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [tempUser, setTempUser] = useState(null);
  const [useBackupCode, setUseBackupCode] = useState(false);

  // Password reset state
  // view: 'login' | 'forgot' | 'forgot_sent' | 'reset' | 'reset_done'
  const [view, setView] = useState('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState('');

  // If URL has ?token=... we're coming from the email link
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setResetToken(token);
      setView('reset');
    }
  }, [searchParams]);

  // -------------------------------
  // LOGIN SUBMIT
  // -------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axiosClient.post('/auth/login', { username, password });

      if (response.data.requires_mfa) {
        setRequiresMfa(true);
        setTempToken(response.data.access_token);
        setTempUser(response.data.user);
        setLoading(false);
        return;
      }

      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userRole', user.role);
      localStorage.setItem('userCompanyId', user.company_id);


      navigate('/');

    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------
  // MFA SUBMIT
  // -------------------------------
  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // MFA verify should use the backend route: /mfa/verify-login
      const response = await axiosClient.post(
        '/mfa/verify-login',
        { code: mfaCode, is_backup_code: useBackupCode },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userRole', user.role);
      localStorage.setItem('userCompanyId', user.company_id);


      navigate('/');

    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid MFA code.');
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------
  // FORGOT PASSWORD SUBMIT
  // -------------------------------
  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axiosClient.post('/auth/forgot-password', { email: resetEmail });
      setView('forgot_sent');
    } catch {
      // Always show "sent" to avoid user enumeration
      setView('forgot_sent');
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------
  // RESET PASSWORD SUBMIT
  // -------------------------------
  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axiosClient.post('/auth/reset-password', { token: resetToken, new_password: newPassword });
      setView('reset_done');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------
  // JSX (your original UI preserved)
  // -------------------------------
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo-container" style={{ marginBottom: '8px' }}>
            <img
              src="/icsc-logo.png"
              alt="ICSC Logo"
              style={{ width: '200px', height: 'auto', marginBottom: '0px' }}
              onError={(e) => {
                // Fallback to text logo if image doesn't exist
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="logo-icon" style={{ display: 'none' }}>
              <span className="logo-text">{BRAND.short}</span>
            </div>
          </div>
          <p className="tagline">The Standard in Certification</p>
          <h1>{BRAND.name}</h1>
          <p className="subtitle">International Council for Standards & Certification</p>
          <p className="boss-acronym">Business Operations & Standards System</p>
        </div>

        {view === 'login' && requiresMfa ? (
          <form onSubmit={handleMfaSubmit} className="login-form">
            {error && (
              <div className="error-message">
                <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            <div style={{ background: '#FFF9E6', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #D4AF37' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <svg style={{ width: '20px', height: '20px', color: '#D4AF37' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <strong style={{ color: '#1a1a1a' }}>Two-Factor Authentication Required</strong>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#1a1a1a' }}>
                Logged in as <strong>{tempUser?.username}</strong>. Enter your MFA code to continue.
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="mfaCode">
                {useBackupCode ? 'Backup Code' : 'Authenticator Code'}
              </label>
              <input
                type="text"
                id="mfaCode"
                value={mfaCode}
                onChange={(e) => setMfaCode(useBackupCode ? e.target.value.toUpperCase().slice(0, 8) : e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                placeholder={useBackupCode ? 'ABCD1234' : '000000'}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '18px',
                  textAlign: 'center',
                  letterSpacing: useBackupCode ? '4px' : '8px',
                  fontFamily: 'monospace',
                  border: '1px solid #ccc',
                  borderRadius: '8px'
                }}
              />
              <div style={{ marginTop: '10px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => {
                    setUseBackupCode(!useBackupCode);
                    setMfaCode('');
                    setError('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#B8860B',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textDecoration: 'underline'
                  }}
                >
                  {useBackupCode ? 'Use Authenticator Code' : 'Use Backup Code'}
                </button>
              </div>
            </div>

            <button type="submit" className="login-button" disabled={loading || (useBackupCode ? mfaCode.length !== 8 : mfaCode.length !== 6)}>
              {loading ? (
                <span className="loading-spinner">
                  <svg className="spinner" viewBox="0 0 24 24">
                    <circle className="spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
                  </svg>
                  Verifying...
                </span>
              ) : (
                'Verify & Continue'
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setRequiresMfa(false);
                setMfaCode('');
                setTempToken('');
                setTempUser(null);
                setUseBackupCode(false);
                setError('');
              }}
              style={{
                width: '100%',
                padding: '12px',
                marginTop: '10px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Back to Login
            </button>
          </form>
        ) : view === 'login' ? (
          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="error-message">
                <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="username">Username</label>
              <div className="input-wrapper">
                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  placeholder="Enter your username"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? (
                <span className="loading-spinner">
                  <svg className="spinner" viewBox="0 0 24 24">
                    <circle className="spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>

            <div style={{ textAlign: 'center', marginTop: '14px' }}>
              <button
                type="button"
                onClick={() => { setView('forgot'); setError(''); }}
                style={{ background: 'none', border: 'none', color: '#B8860B', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline' }}
              >
                Forgot your password?
              </button>
            </div>
          </form>
        ) : null}

        {/* ── Forgot Password form ── */}
        {view === 'forgot' && (
          <form onSubmit={handleForgot} className="login-form">
            {error && (
              <div className="error-message">
                <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}
            <div style={{ marginBottom: '20px', fontSize: '14px', color: '#555', lineHeight: 1.6 }}>
              Enter the email address for your account and we'll send you a reset link.
            </div>
            <div className="form-group">
              <label htmlFor="resetEmail">Email Address</label>
              <div className="input-wrapper">
                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <input
                  type="email"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  disabled={loading}
                />
              </div>
            </div>
            <button type="submit" className="login-button" disabled={loading || !resetEmail}>
              {loading ? (
                <span className="loading-spinner">
                  <svg className="spinner" viewBox="0 0 24 24">
                    <circle className="spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
                  </svg>
                  Sending...
                </span>
              ) : 'Send Reset Link'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '14px' }}>
              <button type="button" onClick={() => { setView('login'); setError(''); }} style={{ background: 'none', border: 'none', color: '#B8860B', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline' }}>
                Back to Sign In
              </button>
            </div>
          </form>
        )}

        {/* ── Reset link sent confirmation ── */}
        {view === 'forgot_sent' && (
          <div className="login-form">
            <div style={{ textAlign: 'center', padding: '10px 0 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📧</div>
              <h3 style={{ margin: '0 0 10px', color: '#1a1a1a' }}>Check your email</h3>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.6 }}>
                If <strong>{resetEmail}</strong> is registered, a password reset link has been sent.
                The link is valid for <strong>1 hour</strong>.
              </p>
              <p style={{ fontSize: '13px', color: '#888', marginTop: '12px' }}>
                Didn't receive it? Check your spam folder, or try again.
              </p>
            </div>
            <button type="button" className="login-button" onClick={() => { setView('forgot'); setError(''); }}>
              Try Again
            </button>
            <div style={{ textAlign: 'center', marginTop: '14px' }}>
              <button type="button" onClick={() => { setView('login'); setError(''); }} style={{ background: 'none', border: 'none', color: '#B8860B', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline' }}>
                Back to Sign In
              </button>
            </div>
          </div>
        )}

        {/* ── Reset Password form (from email link) ── */}
        {view === 'reset' && (
          <form onSubmit={handleReset} className="login-form">
            {error && (
              <div className="error-message">
                <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}
            <div style={{ marginBottom: '20px', fontSize: '14px', color: '#555' }}>
              Enter your new password below.
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="input-wrapper">
                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  placeholder="New password"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm new password"
                  disabled={loading}
                />
              </div>
            </div>
            <button type="submit" className="login-button" disabled={loading || !newPassword || !confirmPassword}>
              {loading ? (
                <span className="loading-spinner">
                  <svg className="spinner" viewBox="0 0 24 24">
                    <circle className="spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
                  </svg>
                  Updating...
                </span>
              ) : 'Set New Password'}
            </button>
          </form>
        )}

        {/* ── Reset done ── */}
        {view === 'reset_done' && (
          <div className="login-form">
            <div style={{ textAlign: 'center', padding: '10px 0 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
              <h3 style={{ margin: '0 0 10px', color: '#1a1a1a' }}>Password updated</h3>
              <p style={{ fontSize: '14px', color: '#555' }}>
                Your password has been changed successfully. You can now sign in.
              </p>
            </div>
            <button type="button" className="login-button" onClick={() => { setView('login'); setError(''); setNewPassword(''); setConfirmPassword(''); }}>
              Sign In
            </button>
          </div>
        )}

        <div className="login-footer">
          <div style={{ marginTop: '16px', fontSize: '12px', color: '#333', lineHeight: 1.5, textAlign: 'center' }}>
            <div>
              <a href="/privacy" style={{ color: '#333', textDecoration: 'underline' }}>Privacy Policy</a>
              {' '}·{' '}
              <a href="/terms" style={{ color: '#333', textDecoration: 'underline' }}>Terms of Service</a>
            </div>
            <div style={{ marginTop: '6px' }}>
              {BRAND.name} is proprietary software owned by {BRAND.short} - International Council for Standards & Certification.
            </div>
          </div>
        </div>
      </div>
      
      <div className="background-pattern"></div>
    </div>
  );
}

export default Login;
